import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import type { FlowInProgress } from "@redotech/redo-flows-common/flow-in-progress";
import { SchemaType } from "@redotech/redo-flows-common/schemas/schemas";
import { recognizeFlow } from "@redotech/redo-recognize-actions-common/actions-flow";
import type { RecognizeActionsEditorProps } from "@redotech/redo-recognize-app/actions-editor-context";
import { Flex } from "@redotech/redo-web/flex";
import { UnsavedChangesModal } from "@redotech/redo-web/unsaved-changes-guard";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import { memo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FlowEditor } from "../../../setting/flow-editor/advanced-flow-editor/flow-editor";
import * as css from "./recognize-actions-editor.module.css";

export const RecognizeActionsEditor = memo(function RecognizeActionsEditor(
  props: RecognizeActionsEditorProps,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [editorVersion, setEditorVersion] = useState(0);
  const [flow, setFlow] = useState<FlowInProgress | null>(() => ({
    _id: "recognize-actions",
    name: { value: "Recognize completed", readonly: true },
    category: "Recognize",
    schemaType: SchemaType.RECOGNIZE,
    steps: cloneDeep(props.steps),
  }));

  if (!flow) {
    return null;
  }

  if (props.readonly) {
    return (
      <FlowEditor
        flowInProgress={flow}
        header={{ hide: true }}
        leftPanel={{ hide: true }}
        readonly
      />
    );
  }

  const hasChanges = !isEqual(flow.steps, props.steps);

  function discardEdits() {
    setFlow((currentFlow) => {
      if (!currentFlow) {
        return null;
      }

      return { ...currentFlow, steps: cloneDeep(props.steps) };
    });
    setConfirmCancel(false);
    setEditorVersion((version) => version + 1);

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("selected");
    setSearchParams(nextSearchParams, { replace: true });
  }

  function cancelEdits() {
    if (hasChanges) {
      setConfirmCancel(true);
      return;
    }

    discardEdits();
  }

  return (
    <Flex className={css.editor} flex={1} minW="none">
      <FlowEditor
        closeButtonText="Cancel"
        flowInProgress={flow}
        hasUnsavedChanges={hasChanges}
        header={{ compact: true, disableButtons: !hasChanges }}
        isModifiedFromStart={false}
        key={editorVersion}
        leftPanel={{
          title: "Steps",
          description: "Check Recognize criteria, then add customer tags.",
          bgColor: "primary",
        }}
        navigationQueryParam="tab"
        onCloseOverride={cancelEdits}
        onSaveOverride={async ({ flowInProgress }) => {
          recognizeFlow.parse(flowInProgress.steps);
          await props.onSave(flowInProgress);
        }}
        rightPanel={{ bgColor: "primary" }}
        saveButtonText="Save new version"
        setFlowInProgress={setFlow}
        stepOptions={[
          {
            steps: [
              AutomationForgeElementType.OMS_CONDITION,
              AutomationForgeElementType.OMS_ACTION,
            ],
          },
        ]}
      />
      {confirmCancel && (
        <UnsavedChangesModal
          onDiscard={discardEdits}
          onKeepEditing={() => setConfirmCancel(false)}
        />
      )}
    </Flex>
  );
});
